import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'

const FAVORITES_EVENT = 'marketplaceFavoritesUpdated'

function storageKey(userId: string): string {
  return `marketplace_favorites_${userId}`
}

function readFavorites(userId: string): string[] {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter((item): item is string => typeof item === 'string')
  } catch {
    return []
  }
}

function writeFavorites(userId: string, favorites: string[]): void {
  localStorage.setItem(storageKey(userId), JSON.stringify(favorites))
  window.dispatchEvent(new Event(FAVORITES_EVENT))
}

export function useMarketplaceFavorites() {
  const { user } = useAuth()
  const userId = user?.id ?? null

  const [favorites, setFavorites] = useState<string[]>(() => {
    if (!userId) return []
    return readFavorites(userId)
  })

  useEffect(() => {
    if (!userId) {
      setFavorites([])
      return
    }
    setFavorites(readFavorites(userId))

    function handleUpdate() {
      setFavorites(readFavorites(userId!))
    }

    window.addEventListener(FAVORITES_EVENT, handleUpdate)
    return () => {
      window.removeEventListener(FAVORITES_EVENT, handleUpdate)
    }
  }, [userId])

  const isFavorite = useCallback(
    (listingId: string): boolean => {
      if (!userId) return false
      return favorites.includes(listingId)
    },
    [favorites, userId]
  )

  const toggle = useCallback(
    (listingId: string): void => {
      if (!userId) return
      const current = readFavorites(userId)
      const next = current.includes(listingId)
        ? current.filter((id) => id !== listingId)
        : [...current, listingId]
      writeFavorites(userId, next)
      setFavorites(next)
    },
    [userId]
  )

  const clear = useCallback((): void => {
    if (!userId) return
    writeFavorites(userId, [])
    setFavorites([])
  }, [userId])

  return { favorites, isFavorite, toggle, clear }
}
